import { useState, useEffect } from 'react';
import axios from 'axios';

import {
  API_URL,
  DANISH,
  ENGLISH,
  DEFAULT_LANGUAGE,
  FIVE_SECONDS,
} from '../constants';

const parseCountryCode = (countryCode) => {
  switch (countryCode) {
    case 'DK':
      return DANISH;
    case 'FR':
    case 'GB':
      return ENGLISH;
    default:
      return DEFAULT_LANGUAGE;
  }
};

export const useFetchLanguage = () => {
  const [language, setLanguage] = useState();

  useEffect(() => {
    axios
      .get('https://geolocation-db.com/json/', { timeout: FIVE_SECONDS })
      .then((response) => {
        const ipData = response.data;
        setLanguage(parseCountryCode(ipData.country_code));
      })
      .catch((error) => {
        console.log('error', error);
        setLanguage(DEFAULT_LANGUAGE);
      });
  }, []);

  return [language, setLanguage];
};

export const useFetchEvents = (language) => {
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(
          `${API_URL}/events?locale=${language}&populate=*&pagination[limit]=-1`,
        )
        .then((response) => {
          const eventsFromResponse = response.data.data.map((d) => ({
            ...d.attributes,
            id: d.id,
          }));
          setEvents(eventsFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setEventsError(true);
        });
    }
  }, [language]);

  return [events, eventsError];
};

export const useFetchExhibitions = (language) => {
  const [exhibitions, setExhibitions] = useState([]);
  const [exhibitionsError, setExhibitionsError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(
          `${API_URL}/exhibitions?locale=${language}&populate=*&pagination[limit]=-1`,
        )
        .then((response) => {
          const exhibitionsFromResponse = response.data.data.map((d) => ({
            ...d.attributes,
            id: d.id,
          }));
          setExhibitions(exhibitionsFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setExhibitionsError(true);
        });
    }
  }, [language]);

  return [exhibitions, exhibitionsError];
};

export const useFetchAbout = (language) => {
  const [about, setAbout] = useState({});
  const [aboutError, setAboutError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(`${API_URL}/about?locale=${language}&populate=*`)
        .then((response) => {
          const aboutFromResponse = response.data.data.attributes;
          aboutFromResponse.id = response.data.data.id;
          setAbout(aboutFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setAboutError(true);
        });
    }
  }, [language]);

  return [about, aboutError];
};

export const useFetchStaticText = (language) => {
  const [staticText, setStaticText] = useState({});
  const [staticTextError, setStaticTextError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(`${API_URL}/static-text?locale=${language}&populate=*`)
        .then((response) => {
          const staticTextFromResponse = response.data.data.attributes;
          staticTextFromResponse.id = response.data.data.id;
          setStaticText(staticTextFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setStaticTextError(true);
        });
    }
  }, [language]);

  return [staticText, staticTextError];
};

export const useMedia = (queries, values, defaultValue) => {
  // Array containing a media query list for each query
  const mediaQueryLists = queries.map(
    (q) => typeof window !== 'undefined' && window.matchMedia(q),
  );

  // Function that gets value based on matching media query
  const getValue = () => {
    // Get index of first media query that matches
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    // Return related value or defaultValue if none
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  };

  // State and setter for matched value
  const [value, setValue] = useState(getValue);

  useEffect(
    () => {
      // Event listener callback
      // Note: By defining getValue outside of useEffect we ensure that it has ...
      // ... current values of hook args (as this hook callback is created once on mount).
      const handler = () => setValue(getValue);
      // Set a listener for each media query with above handler as callback.
      mediaQueryLists.forEach((mql) => mql.addListener(handler));
      // Remove listeners on cleanup
      return () =>
        mediaQueryLists.forEach((mql) => mql.removeListener(handler));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // Empty array ensures effect is only run on mount and unmount
  );

  return value;
};

export const useFetchEducation = (language) => {
  const [education, setEducation] = useState([]);
  const [educationError, setEducationError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(
          `${API_URL}/educations?locale=${language}&populate=*&pagination[limit]=-1`,
        )
        .then((response) => {
          const educationFromResponse = response.data.data.map((d) => ({
            ...d.attributes,
            id: d.id,
          }));
          setEducation(educationFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setEducationError(true);
        });
    }
  }, [language]);

  return [education, educationError];
};

export const useFetchPodcast = (language) => {
  const [podcast, setPodcast] = useState([]);
  const [podcastError, setPodcastError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(
          `${API_URL}/podcasts?locale=${language}&populate=*&pagination[limit]=-1&sort=createdAt:desc`,
        )
        .then((response) => {
          const podcastFromResponse = response.data.data.map((d) => ({
            ...d.attributes,
            id: d.id,
          }));
          setPodcast(podcastFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setPodcastError(true);
        });
    }
  }, [language]);

  return [podcast, podcastError];
};

export const useFetchPublication = (language) => {
  const [publication, setPublication] = useState([]);
  const [publicationError, setPublicationError] = useState(false);

  useEffect(() => {
    if (language) {
      axios
        .get(
          `${API_URL}/publications?locale=${language}&populate=*&pagination[limit]=-1&sort=createdAt:desc`,
        )
        .then((response) => {
          const publicationFromResponse = response.data.data.map((d) => ({
            ...d.attributes,
            id: d.id,
          }));
          setPublication(publicationFromResponse);
        })
        .catch((error) => {
          console.log('error', error);
          setPublicationError(true);
        });
    }
  }, [language]);

  return [publication, publicationError];
};
